import { FloppyDisk } from "@phosphor-icons/react";

/**
 * Section-settings block for the Admin Bundles panel. Two collapsible
 * <details> groups:
 *   1. "Section heading & placement" — heading/subheading text, logo URL,
 *      placement toggle (above / below the Live Catalog), and hide-section
 *      checkbox.
 *   2. "Featured bundle picker" — the single legacy `featured_slug` dropdown
 *      plus the new top-3 `featured_slugs` slot pickers that drive the hero
 *      grid on /shop.
 *
 * Extracted from BundlesPanel.jsx (was ~106 inline lines) so BundlesPanel
 * can focus purely on orchestration (load/save + bundle row rendering).
 */
export default function BundleSectionSettings({ section, setSection, bundles, patchSection }) {
  return (
    <>
      {/* Section-level controls — split into 3 collapsible groups */}
      <details className="border border-white/10 p-4 space-y-3">
        <summary data-testid="admin-bundles-section-header-summary" className="font-display font-black uppercase text-sm tracking-tight cursor-pointer flex items-center gap-2">
          <span>Section heading &amp; placement</span>
          <span className="text-[10px] text-white/40 font-normal">click to expand/collapse</span>
        </summary>
        <div className="grid gap-3 pt-3">
          <label className="block space-y-1">
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">Heading</span>
            <input value={section.heading || ""} onChange={(e) => setSection((s) => ({ ...s, heading: e.target.value }))}
              data-testid="admin-bundles-heading-input"
              className="w-full bg-black border-2 border-white/20 text-white px-3 py-2 text-sm" />
          </label>
          <label className="block space-y-1">
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">Supporting copy</span>
            <textarea rows={2} value={section.subheading || ""} onChange={(e) => setSection((s) => ({ ...s, subheading: e.target.value }))}
              className="w-full bg-black border-2 border-white/20 text-white px-3 py-2 text-sm" />
          </label>
          <label className="block space-y-1">
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">Logo URL (optional — used on the heading and OG bundle card)</span>
            <input value={section.logo_url || ""} placeholder="/sweat-for-sacrifice-mono-navy.png or https://…" onChange={(e) => setSection((s) => ({ ...s, logo_url: e.target.value }))}
              data-testid="admin-bundles-logo-input"
              className="w-full bg-black border-2 border-white/20 text-white px-3 py-2 text-sm font-mono" />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70 mr-2">Placement:</span>
            {["above_catalog", "below_catalog"].map((p) => (
              <button key={p} type="button" onClick={() => setSection((s) => ({ ...s, placement: p }))}
                data-testid={`admin-bundles-placement-${p}`}
                className={`text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-2 border ${section.placement === p ? "bg-[#FF1B91] text-white border-[#FF1B91]" : "border-white/20 text-white/70 hover:border-[#FF1B91]"}`}>
                {p === "above_catalog" ? "Above Live Catalog" : "Below Live Catalog"}
              </button>
            ))}
            <label className="ml-auto inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">
              <input type="checkbox" checked={!!section.hidden} onChange={(e) => setSection((s) => ({ ...s, hidden: e.target.checked }))} data-testid="admin-bundles-section-hidden" />
              Hide entire section
            </label>
          </div>
        </div>
      </details>

      <details className="border border-white/10 p-4 space-y-3">
        <summary data-testid="admin-bundles-featured-summary" className="font-display font-black uppercase text-sm tracking-tight cursor-pointer flex items-center gap-2 flex-wrap">
          <span>Featured bundle picker</span>
          <span className="text-[10px] text-white/40 font-normal">— which 3 bundles show at the top of /shop</span>
        </summary>
        <div className="grid gap-4 pt-3">
          <label className="block space-y-1">
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">Single featured bundle (legacy · used as a fallback badge if the 3-slot picker below is empty)</span>
            <select
              value={section.featured_slug || ""}
              onChange={(e) => setSection((s) => ({ ...s, featured_slug: e.target.value }))}
              data-testid="admin-bundles-featured-slug"
              className="w-full bg-black border-2 border-white/20 text-white px-3 py-2 text-sm"
            >
              <option value="">— None —</option>
              {bundles.map((b) => <option key={b.slug} value={b.slug}>{b.name}</option>)}
            </select>
          </label>
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">Top 3 featured bundles (in display order)</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">These render as the hero grid at the top of /shop, above every other category. Change the slots to reorder or swap featured cards.</p>
            <div className="grid gap-2">
              {[0, 1, 2].map((slot) => {
                const slugs = section.featured_slugs || [];
                const currentSlug = slugs[slot] || "";
                const currentBundle = bundles.find((b) => b.slug === currentSlug);
                return (
                  <div key={slot} className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50 w-16">Slot {slot + 1}</span>
                    <select
                      value={currentSlug}
                      onChange={(e) => {
                        const next = [...(section.featured_slugs || [])];
                        while (next.length <= slot) next.push("");
                        next[slot] = e.target.value;
                        while (next.length && !next[next.length - 1]) next.pop();
                        setSection((s) => ({ ...s, featured_slugs: next }));
                      }}
                      data-testid={`admin-bundles-featured-slug-${slot}`}
                      className="flex-1 min-w-[180px] bg-black border-2 border-white/20 text-white px-3 py-2 text-sm"
                    >
                      <option value="">— None —</option>
                      {bundles.map((b) => (
                        <option key={b.slug} value={b.slug} disabled={slugs.includes(b.slug) && b.slug !== currentSlug}>
                          {b.name}{slugs.includes(b.slug) && b.slug !== currentSlug ? " (already picked)" : ""}
                        </option>
                      ))}
                    </select>
                    {currentBundle && (
                      <span className="text-[10px] uppercase tracking-[0.25em] text-white/60 font-bold">
                        ${Number(currentBundle.price).toFixed(0)} · {(currentBundle.slots || []).length} slots
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <button type="button" onClick={() => patchSection({ heading: section.heading, subheading: section.subheading, logo_url: section.logo_url, placement: section.placement, hidden: !!section.hidden, featured_slug: section.featured_slug || "", featured_slugs: section.featured_slugs || [] })}
            data-testid="admin-bundles-save-section"
            className="inline-flex items-center gap-2 self-start bg-[#FF1B91] hover:bg-[#FF3FA3] text-white px-4 py-2 text-[10px] font-bold uppercase tracking-[0.25em]">
            <FloppyDisk size={12} weight="bold" /> Save section
          </button>
        </div>
      </details>
    </>
  );
}
